import { Fragment, useState } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Label } from '../components/ui/Label';
import { Alert } from '../components/ui/Alert';
import { CampaignStatusBadge } from '../components/CampaignStatusBadge';
import { MockBadge } from '../components/MockBadge';
import { apiFetch } from '../lib/api';
import { useAuth } from '../lib/auth';
import {
  formatDateTime,
  formatNumber,
  formatSendTime,
  fromLocalInputs,
  toLocalInputs,
} from '../lib/format';
import { TOTP_LOCKED_MESSAGE, isTotpLocked } from '../lib/authErrors';
import {
  STEP_UP_ENDED_MESSAGE,
  isStepUpRequired,
  stepUpBody,
  stepUpCodeReady,
  useStepUpWindow,
} from '../lib/stepUp';
import { StepUpCodeField } from '../components/StepUpCodeField';
import { groupCampaigns, type CampaignGroup } from '../lib/campaignGroups';
import { ApiError } from '../types/api';
import type { Campaign, GroupApprovalFailure, PreflightResult } from '../types/api';

/** What prepare answers: the queued recipients and the id of the prepared version (the approval names it). */
interface PrepareResult {
  recipientCount: number;
  snapshotId: string;
}

/** The group the campaign `id` belongs to, or null when it has no sibling languages. */
function findGroup(campaigns: Campaign[], id: string): CampaignGroup | null {
  for (const entry of groupCampaigns(campaigns)) {
    if (entry.kind === 'group' && entry.group.members.some((m) => m.campaign.id === id)) {
      return entry.group;
    }
  }
  return null;
}

/** "All passed", "2 warnings", "1 failing": the preflight of one language in a few words. */
function checksSummary(result: PreflightResult): { text: string; tone: 'ok' | 'warn' | 'fail' } {
  const failing = result.checks.filter((c) => c.status === 'fail').length;
  const warnings = result.checks.filter((c) => c.status === 'warn').length;
  if (failing > 0) return { text: `${failing} failing`, tone: 'fail' };
  if (warnings > 0) {
    return { text: `${warnings} ${warnings === 1 ? 'warning' : 'warnings'}`, tone: 'warn' };
  }
  return { text: 'All passed', tone: 'ok' };
}

const TONE: Record<'ok' | 'warn' | 'fail', string> = {
  ok: 'text-success-text',
  warn: 'text-warning-text',
  fail: 'text-danger-text',
};
const ICON: Record<'ok' | 'warn' | 'fail', string> = { ok: '✓', warn: '!', fail: '✗' };

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof ApiError && err.body.message ? err.body.message : fallback;
}

/**
 * "Send all": every language version of one send ("Black Friday (DA)",
 * "Black Friday (DE)" …) reviewed and approved together with one count and
 * one code. Each language stays its own campaign (own stats, unsubscribes
 * and language gate); the API approves all of them or none
 * (`POST /campaigns/approve-group`). A language that should go at another
 * time is approved on its own send page instead.
 */
export function GruppeSendPage() {
  const { id } = useParams<{ id: string }>();
  const campaignId = id ?? '';
  const { isAdmin, isEditor, user } = useAuth();
  const queryClient = useQueryClient();

  const listQuery = useQuery({
    queryKey: ['campaigns'],
    queryFn: () => apiFetch<{ campaigns: Campaign[] }>('/campaigns'),
  });
  const group = listQuery.data ? findGroup(listQuery.data.campaigns, campaignId) : null;
  const memberIds = group?.members.map((m) => m.campaign.id) ?? [];

  // The single reads carry what the list leaves out (audience), and are
  // what the other pages invalidate after a change.
  const campaignQueries = useQueries({
    queries: memberIds.map((mid) => ({
      queryKey: ['campaign', mid],
      queryFn: () => apiFetch<{ campaign: Campaign }>(`/campaigns/${mid}`),
      refetchInterval: (query: { state: { data: { campaign: Campaign } | undefined } }) => {
        const status = query.state.data?.campaign.status;
        return status === 'scheduled' || status === 'sending' ? 5000 : false;
      },
    })),
  });
  const members = (group?.members ?? []).map((m, i) => ({
    language: m.language,
    campaign: campaignQueries[i]?.data?.campaign ?? m.campaign,
  }));
  const drafts = members.filter((m) => m.campaign.status === 'draft');
  const draftIds = drafts.map((m) => m.campaign.id);

  const preflightQueries = useQueries({
    queries: draftIds.map((did) => ({
      queryKey: ['preflight', did],
      queryFn: () =>
        apiFetch<PreflightResult>(`/campaigns/${did}/preflight`, { method: 'POST', body: {} }),
      staleTime: Infinity,
      // Dropped when the page closes, so every visit checks and prepares
      // anew (like the single send page) instead of reusing an old version.
      gcTime: 0,
      refetchOnWindowFocus: false,
      retry: false,
    })),
  });
  // Prepare is editor+ and draft-only in the API, like on the single send page.
  const prepareQueries = useQueries({
    queries: draftIds.map((did) => ({
      queryKey: ['prepare', did],
      queryFn: async () => {
        const result = await apiFetch<PrepareResult>(`/campaigns/${did}/prepare`, {
          method: 'POST',
        });
        // A prepare that found a change withdraws the test email: read the
        // campaign again so "Sent" is never shown for an older version.
        void queryClient.invalidateQueries({ queryKey: ['campaign', did] });
        return result;
      },
      enabled: isEditor,
      staleTime: Infinity,
      // Dropped when the page closes, so every visit checks and prepares
      // anew (like the single send page) instead of reusing an old version.
      gcTime: 0,
      refetchOnWindowFocus: false,
      retry: false,
    })),
  });
  const prepared = (cid: string) => prepareQueries[draftIds.indexOf(cid)]?.data;
  const preflight = (cid: string) => preflightQueries[draftIds.indexOf(cid)]?.data;
  const allPrepared = drafts.length > 0 && drafts.every((m) => prepared(m.campaign.id));
  const preparing = prepareQueries.some((q) => q.isFetching);
  const prepareFailed = prepareQueries.find((q) => q.isError);
  const total = drafts.reduce((n, m) => n + (prepared(m.campaign.id)?.recipientCount ?? 0), 0);

  const [failures, setFailures] = useState<GroupApprovalFailure[]>([]);
  const failureFor = (cid: string) => failures.find((f) => f.campaignId === cid);

  // --- Test emails: one per language that has none for its prepared version. ---
  const [testError, setTestError] = useState<string | null>(null);
  const missingTests = drafts.filter((m) => !m.campaign.testSendDoneAt);
  const testAllMutation = useMutation({
    mutationFn: async () => {
      for (const m of missingTests) {
        try {
          await apiFetch(`/campaigns/${m.campaign.id}/test-send`, {
            method: 'POST',
            body: { toEmail: user?.email },
          });
        } catch (err) {
          throw new Error(
            `${m.language}: ${errorMessage(err, 'The test email could not be sent.')}`,
            { cause: err },
          );
        } finally {
          void queryClient.invalidateQueries({ queryKey: ['campaign', m.campaign.id] });
        }
      }
    },
    onMutate: () => setTestError(null),
    // The listed failures were for the versions before; the next approval says anew.
    onSuccess: () => setFailures([]),
    onError: (err) => setTestError(err instanceof Error ? err.message : String(err)),
  });

  // --- One send time for every language (an editor sets it on each draft). ---
  const times = drafts.map((m) => m.campaign.scheduledAt);
  const sameTime = times.every((t) => t === times[0]);
  const sharedTime = sameTime ? (times[0] ?? null) : null;
  const [schedDate, setSchedDate] = useState('');
  const [schedTime, setSchedTime] = useState('');
  const [timeError, setTimeError] = useState<string | null>(null);
  const pickedTime = fromLocalInputs(schedDate, schedTime);
  const setTimeMutation = useMutation({
    mutationFn: async (at: number) => {
      for (const m of drafts) {
        try {
          await apiFetch(`/campaigns/${m.campaign.id}`, {
            method: 'PATCH',
            body: { scheduledAt: at },
          });
        } catch (err) {
          throw new Error(`${m.language}: ${errorMessage(err, 'The send time was not saved.')}`, {
            cause: err,
          });
        } finally {
          void queryClient.invalidateQueries({ queryKey: ['campaign', m.campaign.id] });
        }
      }
    },
    onMutate: () => setTimeError(null),
    onSuccess: () => {
      setSchedDate('');
      setSchedTime('');
      setFailures([]);
    },
    onError: (err) => setTimeError(err instanceof Error ? err.message : String(err)),
  });

  // --- The approval: one count, one code, every draft or none. ---
  const [confirmCount, setConfirmCount] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const stepUp = useStepUpWindow();
  const codeBody = stepUp.active ? {} : stepUpBody(totpCode);
  const [approveError, setApproveError] = useState<string | null>(null);
  const [approved, setApproved] = useState<{ count: number; at: number | null } | null>(null);

  const approveMutation = useMutation({
    mutationFn: () =>
      apiFetch('/campaigns/approve-group', {
        method: 'POST',
        // Each language names the version, send time and count this page
        // shows; the API refuses all of them if any has changed since.
        body: {
          ...codeBody,
          items: drafts.map((m) => ({
            campaignId: m.campaign.id,
            snapshotId: prepared(m.campaign.id)?.snapshotId,
            scheduledAt: m.campaign.scheduledAt,
            expectedRecipientCount: prepared(m.campaign.id)?.recipientCount ?? 0,
          })),
          expectedTotalRecipientCount: total,
        },
      }),
    onSuccess: () => {
      if (!stepUp.active) stepUp.onStepUpSuccess();
      // Kept here: once approved, the languages are no longer drafts.
      setApproved({ count: drafts.length, at: sharedTime });
      setApproveError(null);
      setFailures([]);
      setConfirmCount('');
      setTotpCode('');
      for (const cid of draftIds) {
        void queryClient.invalidateQueries({ queryKey: ['campaign', cid] });
      }
      void queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    },
    onError: (err) => {
      setApproveError(
        isTotpLocked(err)
          ? TOTP_LOCKED_MESSAGE
          : isStepUpRequired(err)
            ? STEP_UP_ENDED_MESSAGE
            : errorMessage(err, 'The code is wrong, or the request could not be completed.'),
      );
      stepUp.onStepUpError(err);
      const listed = err instanceof ApiError ? (err.body.failures ?? []) : [];
      setFailures(listed);
      if (err instanceof ApiError && (err.status === 409 || err.status === 400)) {
        // Something changed under the page: read every language again and
        // make the admin confirm the (possibly new) total anew.
        setConfirmCount('');
        for (const cid of draftIds) {
          void queryClient.invalidateQueries({ queryKey: ['campaign', cid] });
        }
        for (const f of listed.filter(
          (f) => f.error === 'campaign_changed' || f.error === 'recipient_count_changed',
        )) {
          void queryClient.invalidateQueries({ queryKey: ['prepare', f.campaignId] });
          void queryClient.invalidateQueries({ queryKey: ['preflight', f.campaignId] });
        }
      }
    },
  });

  const now = Date.now();
  const schedulePassed = drafts.filter(
    (m) => m.campaign.scheduledAt != null && m.campaign.scheduledAt <= now,
  );
  const allTested = drafts.length > 0 && missingTests.length === 0;
  const confirmMatches = confirmCount.trim() === String(total) && total > 0;
  const canApprove =
    drafts.length >= 2 &&
    allPrepared &&
    !preparing &&
    allTested &&
    schedulePassed.length === 0 &&
    confirmMatches &&
    (stepUp.active || stepUpCodeReady(totpCode));

  // The "skip contacts who ordered since" date, shown once when every language has the same.
  const orderedSince = drafts.map((m) => m.campaign.audience?.excludeOrderedSince ?? null);
  const anyOrderedSince = orderedSince.some((t) => t !== null);
  const sameOrderedSince = orderedSince.every((t) => t === orderedSince[0]);

  if (listQuery.isLoading) return <div className="text-muted">Loading campaigns…</div>;
  if (!listQuery.data) return <Alert>The campaigns could not be loaded.</Alert>;
  if (!group) {
    return (
      <Alert>
        This campaign has no other language versions. Approve it on{' '}
        <Link to={`/kampagner/${campaignId}/send`}>its own send page</Link>.
      </Alert>
    );
  }

  return (
    <>
      <header className="flex items-center gap-3">
        <Link to="/kampagner" className="no-underline">
          ← Campaigns
        </Link>
        <MockBadge />
      </header>

      <nav aria-label="Languages" className="flex flex-wrap gap-1.5 -mb-1">
        {members.map((m) => (
          <Link
            key={m.campaign.id}
            to={`/kampagner/${m.campaign.id}`}
            aria-label={`Edit ${m.campaign.name}`}
            className="font-mono text-[12px] font-medium px-2.5 py-1.5 rounded-lg border border-border text-muted no-underline hover:border-accent hover:text-link"
          >
            {m.language}
          </Link>
        ))}
        <span
          aria-current="page"
          className="text-[12px] font-medium px-2.5 py-1.5 rounded-lg border border-accent text-link"
        >
          Send all
        </span>
      </nav>

      <section
        aria-labelledby="group-heading"
        className="border border-border rounded-[14px] p-[22px] flex flex-col gap-4 max-w-[860px]"
      >
        <div>
          <h2 id="group-heading" className="text-xl font-semibold tracking-tight">
            Send all languages of "{group.name}"
          </h2>
          <p className="mt-1.5 text-muted" data-testid="group-lead">
            {drafts.length >= 2
              ? `${drafts.length} languages, ${allPrepared ? formatNumber(total) : '…'} people in all. Approved together, with one code. Sending cannot be recalled.`
              : drafts.length === 1
                ? `Only ${drafts[0]!.language} is still a draft.`
                : 'Every language has been approved or sent.'}
          </p>
        </div>

        {approved !== null ? (
          <div
            role="status"
            className="rounded-xl border border-border bg-bg px-4 py-3 text-sm"
            data-testid="group-approved"
          >
            {`All ${approved.count} languages are approved.`}{' '}
            {approved.at !== null && approved.at > now
              ? `They send ${formatSendTime(approved.at)}.`
              : 'Each sends at its own time, or within a minute.'}
          </div>
        ) : null}

        <div className="overflow-x-auto">
          <table className="w-full text-[13px] border-collapse">
            <thead>
              <tr className="text-left text-muted border-b border-border">
                <th scope="col" className="py-2 pr-3 font-medium">
                  Language
                </th>
                <th scope="col" className="py-2 pr-3 font-medium">
                  Checks
                </th>
                <th scope="col" className="py-2 pr-3 font-medium">
                  Test email
                </th>
                <th scope="col" className="py-2 font-medium text-right">
                  Recipients
                </th>
              </tr>
            </thead>
            <tbody>
              {members.map((m) => {
                const c = m.campaign;
                const isDraft = c.status === 'draft';
                const pre = preflight(c.id);
                const summary = pre ? checksSummary(pre) : null;
                const count = prepared(c.id)?.recipientCount;
                const failure = failureFor(c.id);
                return (
                  <Fragment key={c.id}>
                    <tr
                      className="border-b border-border-soft align-middle"
                      data-testid={`group-row-${m.language}`}
                    >
                      <th scope="row" className="py-2.5 pr-3 text-left font-normal">
                        <Link
                          to={`/kampagner/${c.id}/send`}
                          aria-label={`Open the send page of ${c.name}`}
                          className="font-mono font-medium"
                        >
                          {m.language}
                        </Link>
                      </th>
                      <td className="py-2.5 pr-3">
                        {!isDraft ? (
                          <CampaignStatusBadge status={c.status} />
                        ) : summary ? (
                          <span className={TONE[summary.tone]}>
                            <span aria-hidden="true">{ICON[summary.tone]} </span>
                            {summary.text}
                          </span>
                        ) : (
                          <span className="text-muted">Running…</span>
                        )}
                      </td>
                      <td className="py-2.5 pr-3">
                        {!isDraft ? (
                          <span className="text-muted">–</span>
                        ) : c.testSendDoneAt ? (
                          <span className="text-success-text">
                            <span aria-hidden="true">✓ </span>Sent
                          </span>
                        ) : (
                          <span className="text-warning-text">
                            <span aria-hidden="true">! </span>Not sent
                          </span>
                        )}
                      </td>
                      <td className="py-2.5 text-right tabular-nums">
                        {!isDraft ? (
                          <span className="text-muted">–</span>
                        ) : count !== undefined ? (
                          formatNumber(count)
                        ) : (
                          '…'
                        )}
                      </td>
                    </tr>
                    {failure ? (
                      <tr className="border-b border-border-soft">
                        <td colSpan={4} className="py-2 text-danger-text" role="alert">
                          {`${m.language}: ${failure.message}`}
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                );
              })}
              {drafts.length >= 2 ? (
                <tr className="font-semibold">
                  <th scope="row" className="py-2.5 pr-3 text-left">
                    Total
                  </th>
                  <td className="py-2.5 pr-3" />
                  <td className="py-2.5 pr-3 font-normal">
                    {allTested ? null : (
                      <span className="text-muted">{`${missingTests.length} missing`}</span>
                    )}
                  </td>
                  <td className="py-2.5 text-right tabular-nums" data-testid="group-total">
                    {allPrepared ? formatNumber(total) : '…'}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>

        {prepareFailed ? (
          <Alert>
            {errorMessage(
              prepareFailed.error,
              'The recipients of a language could not be prepared. Check its sender and content, and try again.',
            )}
          </Alert>
        ) : null}

        {drafts.length >= 2 ? (
          <>
            {isEditor && user?.email ? (
              <div className="flex flex-col gap-1.5">
                <div className="flex flex-wrap items-center gap-2.5">
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={
                      missingTests.length === 0 ||
                      !allPrepared ||
                      preparing ||
                      testAllMutation.isPending
                    }
                    onClick={() => testAllMutation.mutate()}
                  >
                    {testAllMutation.isPending
                      ? 'Sending…'
                      : missingTests.length === 0
                        ? 'Every language has a test email'
                        : `Send ${missingTests.length === drafts.length ? 'all' : 'the missing'} test emails to me`}
                  </Button>
                  <span className="text-xs text-muted">
                    One email per language to {user.email}.
                  </span>
                </div>
                {testError ? <Alert>{testError}</Alert> : null}
              </div>
            ) : null}

            <div className="flex flex-col gap-2 text-[13px]">
              <div
                className="flex gap-2.5 items-start px-3 py-2.5 border border-border rounded-[10px]"
                data-testid="group-send-time"
              >
                <span
                  className={
                    schedulePassed.length > 0 || !sameTime
                      ? 'text-warning-text'
                      : 'text-success-text'
                  }
                  aria-hidden="true"
                >
                  {schedulePassed.length > 0 || !sameTime ? '!' : '✓'}
                </span>
                <span>
                  {schedulePassed.length > 0
                    ? `The planned send time has passed for ${schedulePassed.map((m) => m.language).join(', ')}. Pick a new time below.`
                    : !sameTime
                      ? `The languages have different send times: ${drafts
                          .map(
                            (m) =>
                              `${m.language} ${m.campaign.scheduledAt === null ? 'right after approval' : formatSendTime(m.campaign.scheduledAt)}`,
                          )
                          .join('; ')}.`
                      : sharedTime === null
                        ? 'All languages send right after approval.'
                        : `All languages send ${formatSendTime(sharedTime)}.`}
                </span>
              </div>
              {anyOrderedSince ? (
                <div
                  className="flex gap-2.5 items-start px-3 py-2.5 border border-border rounded-[10px]"
                  data-testid="group-ordered-since"
                >
                  <span className="text-success-text" aria-hidden="true">
                    ✓
                  </span>
                  <span>
                    {sameOrderedSince
                      ? `Skips contacts who ordered since ${formatDateTime(orderedSince[0] as number)}, also orders placed after approval`
                      : `Skips contacts who ordered since: ${drafts
                          .map((m, i) =>
                            orderedSince[i] === null
                              ? `${m.language} not set`
                              : `${m.language} ${formatDateTime(orderedSince[i] as number)}`,
                          )
                          .join('; ')}`}
                  </span>
                </div>
              ) : null}
            </div>

            {isEditor ? (
              <fieldset className="flex flex-col gap-1.5 border-0 p-0 m-0">
                <legend className="text-sm font-medium mb-1.5">Send time (all languages)</legend>
                <div className="flex flex-wrap items-end gap-2">
                  <div className="flex flex-col gap-1">
                    <Label htmlFor="group-date">Date</Label>
                    <Input
                      id="group-date"
                      type="date"
                      value={schedDate}
                      placeholder={sharedTime !== null ? toLocalInputs(sharedTime).date : undefined}
                      onChange={(e) => setSchedDate(e.target.value)}
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <Label htmlFor="group-time">Time</Label>
                    <Input
                      id="group-time"
                      type="time"
                      value={schedTime}
                      onChange={(e) => setSchedTime(e.target.value)}
                    />
                  </div>
                  <Button
                    variant="secondary"
                    disabled={pickedTime === null || pickedTime <= now || setTimeMutation.isPending}
                    onClick={() => pickedTime !== null && setTimeMutation.mutate(pickedTime)}
                  >
                    {setTimeMutation.isPending ? 'Saving…' : 'Use for all languages'}
                  </Button>
                </div>
                <div className="min-h-[16px] text-xs text-muted">
                  {pickedTime !== null && pickedTime <= now
                    ? 'Pick a time in the future.'
                    : 'A language that should send at another time is approved on its own page.'}
                </div>
                {timeError ? <Alert>{timeError}</Alert> : null}
              </fieldset>
            ) : null}

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="group-confirm-count">
                Type {allPrepared ? formatNumber(total) : '…'} to confirm
              </Label>
              <Input
                id="group-confirm-count"
                value={confirmCount}
                onChange={(e) => setConfirmCount(e.target.value)}
                disabled={!isAdmin}
              />
              <div className="min-h-[16px] text-xs text-danger-text">
                {confirmCount.length > 0 && !confirmMatches ? 'The number does not match.' : ''}
              </div>
            </div>
            <StepUpCodeField
              id="group-approve-totp"
              value={totpCode}
              onChange={setTotpCode}
              active={stepUp.active}
              minutesLeft={stepUp.minutesLeft}
              disabled={!isAdmin}
            />

            {approveError ? <Alert>{approveError}</Alert> : null}

            <div className="flex flex-col gap-1.5">
              {isAdmin ? (
                <Button
                  disabled={!canApprove || approveMutation.isPending}
                  onClick={() => approveMutation.mutate()}
                >
                  {approveMutation.isPending
                    ? 'Approving…'
                    : `Approve all ${drafts.length} languages`}
                </Button>
              ) : (
                <p className="m-0 text-muted text-sm">Only an admin can approve sending.</p>
              )}
              {isAdmin && !allTested ? (
                <p className="m-0 text-muted text-xs">
                  Locked until every language has a test email.
                </p>
              ) : null}
            </div>
          </>
        ) : drafts.length === 1 ? (
          <p className="m-0 text-sm">
            Approve it on{' '}
            <Link to={`/kampagner/${drafts[0]!.campaign.id}/send`}>its own send page</Link>.
          </p>
        ) : null}
      </section>
    </>
  );
}
